import React, { useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { motion } from 'framer-motion';

interface QRCodeGeneratorProps {
  transacaoData: {
    paymentIntentId: string;
    valor: number;
    numeroComanda?: string;
    operadorId: string;
    unidadeId: string;
    timestamp: string;
    tipoOperacao?: 'debito'; // ← ADICIONADO para compatibilidade
  };
  // Conteúdo pronto do QR (cobrança). Sem ele, monta o QR de débito antigo.
  valorQr?: string;
  onDownload?: () => void;
  tamanho?: number;
  nivelCorrecaoErro?: 'L' | 'M' | 'Q' | 'H';
  corFundo?: string;
  corFrente?: string;
  titulo?: string;
  subtitulo?: string;
  nomeArquivoDownload?: string;
}

const QRCodeGenerator: React.FC<QRCodeGeneratorProps> = ({
  transacaoData,
  valorQr,
  tamanho = 256,
  nivelCorrecaoErro = 'L',
  corFundo = '#FFFFFF',
  corFrente = '#000000',
  titulo = 'QR Code para Débito',
  subtitulo = 'O cliente deve escanear para autorizar o débito',
  nomeArquivoDownload = 'qrcode-debito.svg',
}) => {
  const qrCodeRef = useRef<HTMLDivElement>(null);

  // **VALOR SIMPLIFICADO PARA DÉBITO - COMPATÍVEL COM SCANNER**
  const gerarValorQR = useCallback(() => {
    // Estrutura otimizada para o scanner
    const dadosDebito = {
      t: 'tucoins_debito', // Tipo fixo para débito
      id: transacaoData.paymentIntentId,
      v: transacaoData.valor,
      cmd: transacaoData.numeroComanda || '',
      op: transacaoData.operadorId,
      un: transacaoData.unidadeId,
      ts: transacaoData.timestamp,
      tipoOperacao: 'debito' // ← GARANTIR compatibilidade
    };

    return JSON.stringify(dadosDebito);
  }, [transacaoData]);

  const qrValue = valorQr ?? gerarValorQR();

  // Validar dados obrigatórios
  const validarDadosQR = () => {
    if (valorQr) return true;
    try {
      const dados = JSON.parse(qrValue);
      const valido = dados && dados.id && dados.v && dados.op && dados.un;
      return valido;
    } catch (error) {
      console.error('❌ Erro na validação QR Code:', error);
      return false;
    }
  };

  if (!transacaoData || !transacaoData.valor || !transacaoData.paymentIntentId) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="p-2 bg-background text-text rounded-lg shadow-xl border border-danger/30 flex flex-col items-center justify-center space-y-1 max-w-sm mx-auto"
      >
        <p className="text-lg font-semibold text-danger">Dados incompletos</p>
        <p className="text-sm text-text">
          {!transacaoData?.valor ? 'Informe o valor do débito.' : 'ID da transação é obrigatório.'}
        </p>
      </motion.div>
    );
  }

  if (!validarDadosQR()) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="p-2 bg-background text-text rounded-lg shadow-xl border border-danger/30 flex flex-col items-center justify-center space-y-1 max-w-sm mx-auto"
      >
        <p className="text-lg font-semibold text-danger">Erro na geração</p>
        <p className="text-sm text-text">Dados inválidos para gerar QR Code.</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="p-2 bg-background text-text rounded-xl shadow-2xl border border-primary/20 flex flex-col items-center space-y-1 max-w-md mx-auto transform hover:scale-[1.01] transition-transform duration-300 ease-in-out"
    >
      <h2 className="text-3xl font-bold text-primary text-center leading-tight">
        {titulo}
      </h2>
      {subtitulo && (
        <p className="text-sm text-text-secondary text-center max-w-xs">
          {subtitulo}
        </p>
      )}

      <motion.div
        initial={{ opacity: 0, rotateY: 90 }}
        animate={{ opacity: 1, rotateY: 0 }}
        transition={{ delay: 0.3, duration: 0.8, type: "spring", stiffness: 100 }}
        className="p-4 bg-white rounded-lg shadow-inner border border-gray-200"
        ref={qrCodeRef}
      >
        <QRCodeSVG
          value={qrValue}
          size={tamanho}
          level={nivelCorrecaoErro}
          bgColor={corFundo}
          fgColor={corFrente}
          includeMargin={false}
        />
      </motion.div>

      <div className="text-center">
        <p className="text-lg font-semibold text-primary">
          Débito de {' '}
          {new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }).format(transacaoData.valor)}
        </p>
        {transacaoData.numeroComanda && (
          <p className="text-sm text-text-secondary mt-1">
            Comanda: {transacaoData.numeroComanda}
          </p>
        )}
        
      </div>

    
    </motion.div>
  );
};

export default QRCodeGenerator;